import ScheduledMeetingActions from "./ScheduledMeetingActions";
import ActivityGroups from "./ActivityGroups";
import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FigmaIcon from "./icons/FigmaIcon";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import { EmailOutlined as EmailOutlinedIcon } from "./icons/FigmaControlIcons";
import { BoltOutlined as BoltOutlinedIcon } from "./icons/FigmaControlIcons";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";

import { toLeadActivityItem } from "../utils/entityActivities";
import { toTimeValue } from "../utils/dateTime";
import {
  activityToolbarRowSx,
  activityToolbarDropdownGroupSx,
  activityToolbarMenuItemSx,
  activityToolbarSelectSx,
  activityToolbarTextFieldSx,
} from "./activityToolbarStyles";
import { TAB_VIEW_CONTENT_WRAPPER_SX } from "./tabViewContentSpacing";
import MentionableNoteField from "./MentionableNoteField";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

const MUTED = "#98A2B3";
const BODY = "#475467";
const LINK = "#9C4FFF";
const ACTIVITY_HEADING_SX = {
  color: "#3451F4",
  fontSize: 16,
  fontWeight: 700,
  lineHeight: "24px",
};
const ACTIVITY_PARAGRAPH_SX = {
  fontSize: 14,
  fontWeight: 400,
};

function ActivityTypeIcon({ type }) {
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        display: "grid",
        placeItems: "center",
        flex: "0 0 auto",
      }}
    >
      {type === "email" ? (
        <EmailOutlinedIcon sx={{ width: 24, height: 24, color: "#3451F4" }} />
      ) : type === "note" ? (
        <NoteAltOutlinedIcon sx={{ width: 24, height: 24, color: "#3451F4" }} />
      ) : (
        <BoltOutlinedIcon sx={{ width: 24, height: 24, color: "#3451F4" }} />
      )}
    </Box>
  );
}

const getToken = () => {
  const direct =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("idToken") ||
    localStorage.getItem("id_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("idToken") ||
    sessionStorage.getItem("id_token");

  if (direct && String(direct).trim()) return String(direct).trim();

  const tryJsonKeys = ["user", "auth", "session", "profile"];
  for (const key of tryJsonKeys) {
    try {
      const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const token =
        parsed?.token ||
        parsed?.accessToken ||
        parsed?.access_token ||
        parsed?.jwt ||
        parsed?.idToken ||
        parsed?.id_token;
      if (token && String(token).trim()) return String(token).trim();
    } catch {
      // Ignore malformed cached auth data.
    }
  }

  return "";
};

const getAuthHeaders = () => {
  const token = getToken();
  if (!token) return {};

  const rawToken = token.replace(/^Bearer\s+/i, "");
  return {
    Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
    "x-access-token": rawToken,
  };
};

function BodyWithMentions({ text }) {
  if (!text) return null;

  const parts = String(text).split(/(@[A-Za-z]\w*)/);
  return (
    <Typography
      sx={{
        mt: 0.75,
        ...ACTIVITY_PARAGRAPH_SX,
        color: BODY,
        lineHeight: 1.6,
        whiteSpace: "pre-line",
      }}
    >
      {parts.map((part, index) =>
        part && part.startsWith("@") ? (
          <Box
            key={`${part}-${index}`}
            component="span"
            sx={{ color: "#3451F4", fontWeight: 700 }}
          >
            {part}
          </Box>
        ) : (
          part || ""
        )
      )}
    </Typography>
  );
}

function ActivityLink({ children, onClick, disabled = false }) {
  return (
    <Typography
      onClick={disabled ? undefined : onClick}
      sx={{
        fontSize: 11,
        fontWeight: 600,
        color: disabled ? MUTED : LINK,
        cursor: disabled ? "default" : "pointer",
        userSelect: "none",
        "&:hover": disabled ? undefined : { textDecoration: "underline" },
      }}
    >
      {children}
    </Typography>
  );
}

function AttachmentButton({ attachment }) {
  return (
    <Button
      variant="outlined"
      startIcon={<AttachFileRoundedIcon sx={{ fontSize: 16 }} />}
      onClick={() => attachment?.onClick?.()}
      sx={{
        minHeight: 28,
        px: 1.2,
        borderRadius: "8px",
        textTransform: "none",
        fontSize: 11,
        fontWeight: 600,
        color: LINK,
        borderColor: "rgba(156,79,255,0.35)",
        backgroundColor: "rgba(156,79,255,0.04)",
        "&:hover": {
          borderColor: "rgba(156,79,255,0.5)",
          backgroundColor: "rgba(156,79,255,0.08)",
        },
      }}
    >
      {attachment?.label || "Attachment"}
    </Button>
  );
}

function ActivityRow({ item, onReadMore, onEdit, onDelete, onActivityChange }) {
  return (
    <Box sx={{ px: 2, py: 1.6 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1, display: "flex", alignItems: "flex-start", gap: 1 }}>
          <ActivityTypeIcon type={item?.type} />

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={ACTIVITY_HEADING_SX}>
              {item?.title || item?.typeLabel || "Activity"}
            </Typography>

            {item?.meta?.length ? (
              <Box sx={{ mt: 0.6 }}>
                {item.meta.map((metaItem, index) => (
                  <Typography
                    key={`meta-${index}`}
                    sx={{ fontSize: 11, fontWeight: 700, color: MUTED, lineHeight: 1.5 }}
                  >
                    {metaItem?.label ? (
                      <>
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {metaItem.label}{" "}
                        </Box>
                        {metaItem.value}
                      </>
                    ) : (
                      metaItem?.value || ""
                    )}
                  </Typography>
                ))}
              </Box>
            ) : null}

            {item?.body ? <BodyWithMentions text={item.body} /> : null}

            {item?.attachments?.length ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {item.attachments.map((attachment, index) => (
                  <Box key={`attachment-${index}`} sx={{ mb: 1 }}>
                    <AttachmentButton attachment={attachment} />
                  </Box>
                ))}
              </Stack>
            ) : null}

            {item?.raw?.metadata?.calendar_event_id ? <ScheduledMeetingActions record={{ ...item.raw.metadata, id: item.raw.id, actor_user_id: item.raw.actor_user_id }} onSaved={onActivityChange} /> : (item?.showReadMore || item?.showEdit || item?.showDelete) ? (
              <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                {item.showReadMore ? (
                  <ActivityLink onClick={() => onReadMore?.(item)}>Read More</ActivityLink>
                ) : null}
                {item.showReadMore && (item.showEdit || item.showDelete) ? (
                  <Typography sx={{ fontSize: 11, color: MUTED }}>|</Typography>
                ) : null}
                {item.showEdit ? (
                  <ActivityLink onClick={() => onEdit?.(item)}>Edit</ActivityLink>
                ) : null}
                {item.showEdit && item.showDelete ? (
                  <Typography sx={{ fontSize: 11, color: MUTED }}>|</Typography>
                ) : null}
                {item.showDelete ? (
                  <ActivityLink onClick={() => onDelete?.(item)}>Delete</ActivityLink>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Box>

        <Typography sx={{ pt: 0.1, fontSize: 14, color: MUTED, whiteSpace: "nowrap" }}>
          {item?.dateText || ""}
        </Typography>
      </Box>
    </Box>
  );
}

export default function LeadActivitiesTabContent({
  activities = [],
  entityType = "lead",
  entityId = null,
  onActivityChange,
  readOnly = false,
}) {
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerTitle, setComposerTitle] = useState("");
  const [composerBody, setComposerBody] = useState("");
  const [composerMentionedUserIds, setComposerMentionedUserIds] = useState([]);
  const [composerSaving, setComposerSaving] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [busyDeleteId, setBusyDeleteId] = useState(null);

  const items = useMemo(
    () =>
      (Array.isArray(activities) ? activities : [])
        .map((activity) => { const item = toLeadActivityItem(activity); return item && readOnly ? { ...item, showEdit: false, showDelete: false } : item; })
        .filter(Boolean),
    [activities, readOnly]
  );

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    let output = items;

    if (typeFilter !== "all") {
      output = output.filter((item) => item.type === typeFilter);
    }

    if (normalizedQuery) {
      output = output.filter((item) => {
        const blob = [
          item.title,
          item.typeLabel,
          item.body,
          ...(item.meta || []).map((metaItem) => `${metaItem?.label || ""} ${metaItem?.value || ""}`),
          ...(item.attachments || []).map((attachment) => attachment?.label || ""),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(normalizedQuery);
      });
    }

    const sorted = [...output];
    if (sortBy === "oldest" || sortBy === "newest") {
      sorted.sort((left, right) => {
        const leftTime = toTimeValue(left?.ts);
        const rightTime = toTimeValue(right?.ts);
        return sortBy === "oldest" ? leftTime - rightTime : rightTime - leftTime;
      });
      return sorted;
    }

    sorted.sort((left, right) => {
      const leftTitle = String(left?.title || "").toLowerCase();
      const rightTitle = String(right?.title || "").toLowerCase();
      if (leftTitle < rightTitle) return sortBy === "az" ? -1 : 1;
      if (leftTitle > rightTitle) return sortBy === "az" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [items, query, sortBy, typeFilter]);


  const typeOptions = useMemo(
    () => [
      { value: "all", label: "All Activities" },
      { value: "email", label: "Emails" },
      { value: "action", label: "Actions" },
      { value: "note", label: "Notes" },
    ],
    []
  );

  const sortOptions = useMemo(
    () => [
      { value: "newest", label: "Sort by" },
      { value: "oldest", label: "Oldest" },
      { value: "az", label: "Title (A-Z)" },
      { value: "za", label: "Title (Z-A)" },
    ],
    []
  );

  const resetComposer = useCallback(() => {
    setComposerTitle("");
    setComposerBody("");
    setComposerMentionedUserIds([]);
    setComposerOpen(false);
    setComposerSaving(false);
  }, []);

  const handleCreateNote = useCallback(async () => {
    if (!entityId || !String(composerBody || "").trim() || composerSaving) return;

    setComposerSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/activities`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({
          entityType,
          entityId,
          activityType: "note",
          title: String(composerTitle || "").trim() || "Note",
          summary: String(composerBody || "").trim().split("\n")[0].slice(0, 255),
          details: composerBody,
          mentionedUserIds: composerMentionedUserIds,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to create note (${response.status})`);
      }

      resetComposer();
      onActivityChange?.();
    } catch (error) {
      console.error("Failed to create company note activity:", error);
      window.alert(error?.message || "Failed to create note.");
      setComposerSaving(false);
    }
  }, [composerBody, composerMentionedUserIds, composerSaving, composerTitle, entityId, entityType, onActivityChange, resetComposer]);

  const handleReadMore = useCallback((item) => {
    setViewingItem(item);
  }, []);

  const handleStartEdit = useCallback((item) => {
    setEditingItem(item);
    setEditTitle(String(item?.title || "").trim());
    setEditBody(String(item?.body || "").trim());
  }, []);

  const handleSaveEdit = useCallback(async () => {
    const activityId = Number(editingItem?.id || 0);
    if (!Number.isFinite(activityId) || activityId <= 0 || editSaving) return;

    setEditSaving(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/activities/${encodeURIComponent(activityId)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          credentials: "include",
          body: JSON.stringify({
            activityType: editingItem?.type || "note",
            title: String(editTitle || "").trim() || editingItem?.typeLabel || "Activity",
            summary: String(editBody || "").trim().split("\n")[0].slice(0, 255),
            details: editBody,
          }),
        }
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to update activity (${response.status})`);
      }

      setEditingItem(null);
      setEditTitle("");
      setEditBody("");
      setEditSaving(false);
      onActivityChange?.();
    } catch (error) {
      console.error("Failed to update company activity:", error);
      window.alert(error?.message || "Failed to update activity.");
      setEditSaving(false);
    }
  }, [editBody, editSaving, editTitle, editingItem, onActivityChange]);

  const handleDelete = useCallback(async (item) => {
    const activityId = Number(item?.id || 0);
    if (!Number.isFinite(activityId) || activityId <= 0 || busyDeleteId === activityId) return;

    const confirmed = window.confirm("Delete this activity? This cannot be undone.");
    if (!confirmed) return;

    setBusyDeleteId(activityId);
    try {
      const response = await fetch(
        `${API_BASE}/api/activities/${encodeURIComponent(activityId)}`,
        {
          method: "DELETE",
          headers: {
            ...getAuthHeaders(),
          },
          credentials: "include",
        }
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to delete activity (${response.status})`);
      }

      onActivityChange?.();
    } catch (error) {
      console.error("Failed to delete company activity:", error);
      window.alert(error?.message || "Failed to delete activity.");
    } finally {
      setBusyDeleteId(null);
    }
  }, [busyDeleteId, onActivityChange]);

  return (
    <>
      <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}>
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={() => setFilterAnchorEl(null)}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 2,
              minWidth: 190,
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
            },
          }}
        >
          {typeOptions.map((option) => (
            <MenuItem
              key={`activity-filter-${option.value}`}
              selected={typeFilter === option.value}
              onClick={() => {
                setTypeFilter(option.value);
                setFilterAnchorEl(null);
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>

        <Box sx={{ backgroundColor: "#fff", borderRadius: 2, overflow: "hidden" }}>
          <Box
            sx={{
              px: 0,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              ...activityToolbarRowSx,
            }}
          >
            <TextField
              size="small"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search..."
              sx={activityToolbarTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FigmaIcon name="search" color="#9C4FFF" />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={activityToolbarDropdownGroupSx}>
              <FormControl size="small" sx={{ width: { xs: "100%", md: 324 }, maxWidth: "100%" }}>
                <Select
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                  IconComponent={KeyboardArrowDownRoundedIcon}
                  sx={activityToolbarSelectSx}
                >
                  {typeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={activityToolbarMenuItemSx}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ width: { xs: "100%", md: 324 }, maxWidth: "100%" }}>
                <Select
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                  IconComponent={KeyboardArrowDownRoundedIcon}
                  sx={activityToolbarSelectSx}
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={activityToolbarMenuItemSx}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!readOnly && <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => setComposerOpen(true)}
                disabled={!entityId}
                sx={{
                  height: 32,
                  borderRadius: "999px",
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 600,
                  backgroundColor: LINK,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#8E3DF0",
                    boxShadow: "none",
                  },
                }}
              >
                Add New
              </Button>}
            </Box>
          </Box>

          <Divider sx={{ borderColor: "#EEF2F7" }} />

          <ActivityGroups items={filtered} typeFilter={typeFilter} resetKey={JSON.stringify([entityId, query, sortBy, typeFilter])}
            renderItem={item => <ActivityRow onActivityChange={onActivityChange}
              item={{ ...item, showDelete: item.showDelete && busyDeleteId !== Number(item.id || 0) }}
              onReadMore={handleReadMore} onEdit={handleStartEdit} onDelete={handleDelete} />} />
        </Box>
      </Box>

      <Dialog open={composerOpen} onClose={() => !composerSaving && resetComposer()} fullWidth maxWidth="sm">
        <DialogTitle data-popup-header sx={{ fontSize: 16, fontWeight: 700 }}>Add Note</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              label="Title"
              value={composerTitle}
              onChange={(event) => setComposerTitle(event.target.value)}
              size="small"
              fullWidth
              placeholder="Note"
            />
            <MentionableNoteField
              label="Note"
              value={composerBody}
              onChange={(event) => setComposerBody(event.target.value)}
              onMentionedUserIdsChange={setComposerMentionedUserIds}
              multiline
              minRows={5}
              fullWidth
              placeholder="Add a note for this company activity feed"
            />
          </Stack>
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={resetComposer}
            disabled={composerSaving}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateNote}
            disabled={!String(composerBody || "").trim() || composerSaving}
            sx={{
              textTransform: "none",
              borderRadius: "999px",
              backgroundColor: LINK,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#8E3DF0",
                boxShadow: "none",
              },
            }}
          >
            {composerSaving ? "Saving..." : "Save Note"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(viewingItem)}
        onClose={() => setViewingItem(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle data-popup-header sx={{ fontSize: 16, fontWeight: 700 }}>
          {viewingItem?.title || viewingItem?.typeLabel || "Activity"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 0.5 }}>
            {viewingItem?.meta?.map((metaItem, index) => (
              <Typography key={`view-meta-${index}`} sx={{ fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
                {metaItem?.label ? (
                  <>
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {metaItem.label}{" "}
                    </Box>
                    {metaItem.value}
                  </>
                ) : (
                  metaItem?.value || ""
                )}
              </Typography>
            ))}
            {viewingItem?.body ? <BodyWithMentions text={viewingItem.body} /> : null}
            {viewingItem?.attachments?.length ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                {viewingItem.attachments.map((attachment, index) => (
                  <Box key={`view-attachment-${index}`} sx={{ mb: 1 }}>
                    <AttachmentButton attachment={attachment} />
                  </Box>
                ))}
              </Stack>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewingItem(null)} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingItem)}
        onClose={() => !editSaving && setEditingItem(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle data-popup-header sx={{ fontSize: 16, fontWeight: 700 }}>Edit Activity</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              label="Title"
              value={editTitle}
              onChange={(event) => setEditTitle(event.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Details"
              value={editBody}
              onChange={(event) => setEditBody(event.target.value)}
              multiline
              minRows={5}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEditingItem(null)}
            disabled={editSaving}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={!String(editBody || "").trim() || editSaving}
            sx={{
              textTransform: "none",
              borderRadius: "999px",
              backgroundColor: LINK,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#8E3DF0",
                boxShadow: "none",
              },
            }}
          >
            {editSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
